import {isWedDefaultDisplay, IWedChildrenElt, IWedDisplayElt, IWedletModel, IWedSelector} from "/@back@/edit/wed/wedCore.js";
import {
	findScrollCtn,
	ICharsWedlet,
	IChildWedlet,
	IElementWedlet,
	IFindInAreaWedlet,
	IParentWedlet,
	IS_EltWedlet,
	isDisplayedWedlet,
	ISkAnnotListener,
	isParentWedlet,
	isTargetableWedlet,
	ITargetableWedlet,
	IVirtualisableWedlet,
	IWedlet,
	IWedletArea,
	OFindWedletOptions,
	OVisitWedletOptions,
	WEDLET
} from "/@back@/edit/wed/wedlets/wedlet.js";
import {GFX} from "/@lib@/commons/utils/gfx.js";
import {DOM, ENodeType, EUnknownNodeType} from "/@lib@/commons/xml/dom.js";
import {IJmlNode, IJmlSubSet, JML} from "/@lib@/commons/xml/jml.js";
import {IXAddr, XA} from "/@lib@/commons/xml/xAddr.js";
import {IXmlMsg} from "/@lib@/edit/ot/xmlHouse.js";
import {ISkAnnot, isSkAnnotDrawer} from "/@lib@/edit/schema/schemaAnnots.js";
import {OWedEditorBoxConfig} from "/@back@/edit/wed/wedEditorBox.js";
import {IIntersectHandler, WedMgr} from "/@back@/edit/wed/wedEditor.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";


/**
 * Implémentation commune des Wedlets ne gérant un seul IElementWedlet principal.
 */


/**
 * Wedlets possédant un seul IElementWedlet via une prop .element
 */
export interface IWedletSingleElt extends IWedlet {

	/** Elément Html du wedlet. null si le wedlet est déconnecté de l'arbre (supprimé). */
	element: IElementWedlet | null;

	/**
	 * Elément pour insérer les wedlets fils (cas d'un élément qui crée son contexte de styles pour les descendants).
	 * Retourne this.element si pas de délégation dans un contexte shadow.
	 */
	elementHost: HTMLElement;

	/** Création de l'élément pour ce wedlet à partir de son modèle et insertion dans le contexte parent.
	 * Cette méthode doit être appelée juste avant le bind : IChildWedlet.bindWithNode(), ICharsWedlet.bindWithAttr(),
	 * IVirtualisableWedlet.bindAsVirtual(), IRootWedlet.bindRoot().
	 */
	insertElement?(parent: Element, insertBefore?: Node, slotName?: string, caller?: IWedChildrenElt): void;

	/** Wedlet dans état refermé, les fils sont considérés non visibles. */
	isCollapsed?: boolean
}

export function isWedletSingleElt(wedlet: IWedlet): wedlet is IWedletSingleElt {return wedlet && ("element" in wedlet)}


/** Architecture du wedlet à enrichir via l'agrégation. */
export enum EWedletArch {
	/** Les noeuds HTML servent à naviguer / trouver les wedlets fils.*/
	dom = 0,
	/** Le IWedlet et le IElementWedlet sont le même objet. */
	merged,
	/** La gestion de la navigation est spécifique (fork, ...). */
	custom
}

interface OAgWedletSingleEltConfig {
	wedletArch: EWedletArch;
	isParentWedlet?: boolean;
	isCharsWedlet?: boolean;
	isVirtualisableWedlet?: boolean;
	proxySkAnnots?: boolean;
}

/** Agrégation pour les Wedlets possédant un seul IElementWedlet via une prop .element. */
export function AgWedletSingleElt(cls: any, config: OAgWedletSingleEltConfig): any {
	if (config.isParentWedlet) {
		const proto = cls.prototype as IParentWedlet;
		switch (config.wedletArch) {
		case EWedletArch.dom:
		case EWedletArch.merged:
			proto.findWedletChild = findWedletChild;
			proto.visitWedletChildren = visitWedletChildren;
			break;
		}
		(cls.prototype as IFindInAreaWedlet).findInArea = findInArea;
	}
	if (config.isCharsWedlet) {
		const proto = cls.prototype as ICharsWedlet;
		proto.insertChars = insertChars;
		proto.deleteChars = deleteChars;
		proto.replaceChars = replaceChars;
	}
	if (config.isVirtualisableWedlet) {
		const proto = cls.prototype as IVirtualisableWedlet;
		proto.getVirtualXaPart = getVirtualXaPart;
	}
	if (config.proxySkAnnots) {
		const proto = cls.prototype as ISkAnnotListener;
		proto.onAddedSkAnnot = onAddedSkAnnot;
		proto.onRemovedSkAnnot = onRemovedSkAnnot;
	}
	return cls;
}

function findWedletChild(this: IWedletSingleElt, xaPart: string | number, options?: any): IChildWedlet {
	const parent = this.element?.delegatedHost || this.element;
	for (let ch = parent?.firstElementChild; ch; ch = ch.nextElementSibling) {
		if (IS_EltWedlet(ch) && ch.wedlet.xaPart === xaPart) return ch.wedlet as IChildWedlet;
	}
	return null;
}

function visitWedletChildren(this: IWedletSingleElt & IParentWedlet, from: number, len: number, visitor: (this: null, wedlet: IChildWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void {
	const parent = this.element?.delegatedHost || this.element;
	const end = Math.max(0, from) + len;
	for (let ch = parent?.firstElementChild; ch; ch = ch.nextElementSibling) {
		if (IS_EltWedlet(ch)) {
			const xaPart = ch.wedlet.xaPart;
			if (xaPart == null) {
				if (options && options.includeVirtuals) {
					if (visitor.call(null, ch.wedlet) === 'stop') return 'stop';
				}
			} else if (typeof xaPart === 'number' ? xaPart >= from : from === -1) {
				if (xaPart as number >= end) return;
				if (visitor.call(null, ch.wedlet) === 'stop') return 'stop';
			}
		}
	}
}

function insertChars(this: IWedletSingleElt, from: number, chars: string, msg: IXmlMsg) {this.element.insertChars(from, chars, msg)}

function deleteChars(this: IWedletSingleElt, from: number, len: number, msg: IXmlMsg) {this.element.deleteChars(from, len, msg)}

function replaceChars(this: IWedletSingleElt, chars: string, msg: IXmlMsg) {this.element.replaceChars(chars, msg)}

function onAddedSkAnnot(this: IWedletSingleElt, annot: ISkAnnot) {if (isSkAnnotDrawer(this.element)) this.element.drawAnnot(annot)}

function onRemovedSkAnnot(this: IWedletSingleElt, annot: ISkAnnot) {if (isSkAnnotDrawer(this.element)) this.element.eraseAnnot(annot)}

function getVirtualXaPart(this: IWedletSingleElt): string | number {
	if (this.xaPart != null) return this.xaPart;
	switch (this.model.nodeType) {
	case ENodeType.attribute:
		return this.wedNodeName || this.model.nodeName;
	case ENodeType.document:
		console.error("getVirtualXaPart() on document bind!");
		return null;
	}
	let prevNode = this.element.previousElementSibling;
	while (prevNode) {
		if (IS_EltWedlet(prevNode) && typeof prevNode.wedlet.xaPart === 'number') {
			return prevNode.wedlet.xaPart + 1;
		}
		prevNode = prevNode.previousElementSibling;
	}
	return 0;
}

function findInArea(this: IWedletSingleElt & IFindInAreaWedlet, cb: (wedletArea: IWedletArea) => 'next' | 'nextSibling' | 'stop', area: DOMRectReadOnly, visitOptions?: OVisitWedletOptions): void | 'stop' {
	let rect = this.wedletRect;
	if (rect === undefined) rect = (window.getComputedStyle(this.element).display !== 'contents' ? this.element.getBoundingClientRect() : null);
	let intersectRect: DOMRect;
	if (!rect || (intersectRect = GFX.intersect(rect, area)) != null) {
		switch (cb({wedlet: this, intersectRect})) {
		case 'next':
			//On descend dans fils
			if (isParentWedlet(this) && (this as IParentWedlet).visitWedletChildren(-1, Infinity, (w: IWedlet) => {
				if (WEDLET.findWedletInArea(cb, w, area, visitOptions) === 'stop') return 'stop';
			}, visitOptions) === 'stop') return 'stop';
			return;
		case 'nextSibling':
			return;
		case 'stop':
			return 'stop';
		default:
			throw Error("Unknown findInArea cb return value.");
		}
	}
}


/**
 * Box représentant un élement du document non dessiné.
 */
export class OffView extends HTMLElement implements IElementWedlet, IParentWedlet, IChildWedlet, IWedletSingleElt, IIntersectHandler, ITargetableWedlet {

	get wedlet(): IWedlet {return this}

	get wedMgr(): WedMgr {return this.wedParent.wedMgr}

	get wedAnchor(): IXAddr {return XA.append(this.wedParent.wedAnchor, this.xaPart)}

	xaPart: number;
	wedNodeName: string;
	wedParent: IParentWedlet;

	get model(): IWedletModel {return OFFVIEW_MODEL};

	isVirtual() {return false}

	get element() {return this}

	get elementHost() {return this}

	insertElement(parent: Element, insertBefore?: Node, slotName?: string, caller?: IWedChildrenElt) {throw Error("Should not be used ?")}

	weight: number;

	initViewOff(wedParent: IParentWedlet, xaPart: number, weight: number): this {
		this.wedParent = wedParent;
		this.xaPart = xaPart;
		this.weight = weight;
		//this.style.border = "1px solid red";
		this.style.display = "block";
		this.adjustHeight();
		//console.log("BoxOffView Add:::"+this.wedAnchor+" weight:"+this.weight);
		return this;
	}

	bindWithNode(xaOffset: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {/*not used*/}

	protected adjustHeight() {
		this.style.height = Math.max(1, this.weight / 100) + "em";
	}

	onIntersectChange(entry: IntersectionObserverEntry) {
		if (entry.intersectionRatio === 0) return;
		//console.log("IntersectionObserverEntry::::", entry);
		//console.log("BoxOffView Replace:::"+this.wedAnchor+" weight:"+this.weight);
		if (this.isSync()) this.drawContentSync(OffView.shouldPreserveScroll !== 0);
		else this.drawContentAsync(OffView.shouldPreserveScroll !== 0);
		if (OffView.shouldPreserveScroll !== 0) {
			clearTimeout(OffView.shouldPreserveScroll);
			OffView.shouldPreserveScroll = 0;
		}
	}

	isSync(): boolean {return this.wedMgr.docHolder != null}

	async focusWedlet() {
		if (this.isSync()) this.drawContentSync();
		else await this.drawContentAsync();
		let newWedlet = this.wedParent.findWedletChild(this.xaPart, WEDLET.VISITOPTIONS_mainBranch) as IWedlet;
		while (newWedlet) {
			if (newWedlet.focusWedlet) {
				newWedlet.focusWedlet();
				OffView.shouldPreserveScroll = setTimeout(() => {OffView.shouldPreserveScroll = 0;}, 50) as any /* Bug typage TS */;
				break;
			}
			newWedlet = newWedlet.wedParent;
		}
	}

	async highlightFromLink(blockPos: "nearest" | "start") {
		if (this.isSync()) this.drawContentSync();
		else await this.drawContentAsync();
		let newWedlet = this.wedParent.findWedletChild(this.xaPart, WEDLET.VISITOPTIONS_mainBranch) as IWedlet;
		while (newWedlet) {
			if (isTargetableWedlet(newWedlet)) {
				await newWedlet.highlightFromLink(blockPos);
				OffView.shouldPreserveScroll = setTimeout(() => {OffView.shouldPreserveScroll = 0;}, 50) as any /* Bug typage TS */;
				break;
			}
			newWedlet = newWedlet.wedParent;
		}
	}

	/**
	 * Flag indiquant pendant quelques millisecondes que la position de l'elt focusé devrait être préservée.
	 * Sinon, problèmes à la remontée du scroll par la molette par ex si des BoxOffview intermédiaires sont transformés,
	 * le scroll up est contrecarré.
	 */
	static shouldPreserveScroll: number = 0;

	protected drawContentSync(preserveScroll?: boolean) {
		//console.log("BoxOffView.drawContentSync:::::", preserveScroll);
		if (preserveScroll) this.tryPreserveScroll();
		const xa = this.wedAnchor;
		const wedMgr = this.wedMgr;
		const jml = wedMgr.getContent(xa);
		if (jml && jml.length > 0) {
			this.wedParent.replaceChildBind(this.xaPart, jml[0] as IJmlNode, jml[1] as IJmlSubSet);
			wedMgr.dispatchAnnotsAfterRebuildWedlet(xa);
		} else console.log(`BoxOffView.drawContent : no content at ${xa}`);
	}

	protected async drawContentAsync(preserveScroll?: boolean) {
		//console.log("BoxOffView.drawContentAsync:::::", preserveScroll);
		let xa = this.wedAnchor;
		const wedMgr = this.wedMgr;
		const fetchJml = await wedMgr.fetchContent(xa);
		const jml = fetchJml.result;
		xa = fetchJml.xa; //xa a pu être ajusté par une édition concurrente.
		if (jml && jml.length > 0) {
			if (preserveScroll) this.tryPreserveScroll();
			this.wedParent.replaceChildBind(this.xaPart, jml[0] as IJmlNode, jml[1] as IJmlSubSet);
			wedMgr.dispatchAnnotsAfterRebuildWedlet(xa);
		} else console.log(`BoxOffView.drawContent : no content at ${xa}`);
	}

	/** Tente de préserver la position de l'elt focusé par rapport au viewport, malgré la transformation de BoxOffview. */
	protected tryPreserveScroll() {
		if (!OffView.reqScrollFreeze) {
			const scrollCtn = findScrollCtn(this);
			if (!scrollCtn) return;
			const eltRef = DOMSH.findDeepActiveElement(DOMSH.findDocumentOrShadowRoot(this));
			if (eltRef) {
				const oriSrcollTop = scrollCtn.scrollTop;
				const eltRect = eltRef.getBoundingClientRect();
				OffView.reqScrollFreeze = () => {
					OffView.reqScrollFreeze = null;
					const newEltRect = eltRef.getBoundingClientRect();
					scrollCtn.scrollTop = oriSrcollTop + newEltRect.top - eltRect.top;
				};
				requestAnimationFrame(OffView.reqScrollFreeze);
			}
		}
	}

	static reqScrollFreeze: FrameRequestCallback;

	connectedCallback() {
		this._intersecObs = this.wedMgr.intersectObserve(this);
		if (this._intersecObs == null) {
			//Pas de scroll => pas de notification possible, on construit le contenu de suite.
			if (this.isSync()) this.drawContentSync();
			else this.drawContentAsync();
		}
	}

	protected _intersecObs: IntersectionObserver | null;

	disconnectedCallback() {
		this._intersecObs?.unobserve(this);
	}

	configWedletElt(tpl: Element, wedlet: IWedlet) {}

	refreshBindValue(val: IJmlNode) {
		const node = XA.findDomLast(this.wedAnchor, this.wedMgr.docHolder.getDocument());
		this.weight = JML.computeWeightNode(node);
		this.adjustHeight();
	}

	findWedletChild(xaPart: number | string, options?: OFindWedletOptions): IChildWedlet | IChildWedlet[] {
		if (!options || !options.forceFetch) return null;
		options.contentUpdated = true;
		const prev = DOM.findPreviousSibling(this, IS_EltWedlet);
		const parent = this.parentNode;
		if (this.isSync()) {
			this.drawContentSync();
			const newElt = prev ? DOM.findNextSibling(prev, IS_EltWedlet) as IElementWedlet : DOM.findFirstChild(parent, IS_EltWedlet) as IElementWedlet;
			return newElt && isParentWedlet(newElt.wedlet) ? newElt.wedlet.findWedletChild(xaPart, options) : null;
		} else {
			(options.forceFetchPromises && (options.forceFetchPromises = [])).push(this.drawContentAsync().then(() => {
				const newElt = prev ? DOM.findNextSibling(prev, IS_EltWedlet) as IElementWedlet : DOM.findFirstChild(parent, IS_EltWedlet) as IElementWedlet;
				return newElt && isParentWedlet(newElt.wedlet) ? newElt.wedlet.findWedletChild(xaPart, options) : null;
			}));
		}
	}

	visitWedletChildren(from: number, len: number, visitor: (this: null, wedlet: IChildWedlet) => 'stop' | void, options?: OVisitWedletOptions): 'stop' | void {
		if (!options || !options.forceFetch) return null;
		const prev = DOM.findPreviousSibling(this, IS_EltWedlet);
		const parent = this.parentNode;
		if (this.isSync()) {
			this.drawContentSync();
			const newElt = prev ? DOM.findNextSibling(prev, IS_EltWedlet) as IElementWedlet : DOM.findFirstChild(parent, IS_EltWedlet) as IElementWedlet;
			return newElt && isParentWedlet(newElt.wedlet) ? newElt.wedlet.visitWedletChildren(from, len, visitor) : undefined;
		} else {
			(options.forceFetchPromises && (options.forceFetchPromises = [])).push(this.drawContentAsync().then(() => {
				const newElt = prev ? DOM.findNextSibling(prev, IS_EltWedlet) as IElementWedlet : DOM.findFirstChild(parent, IS_EltWedlet) as IElementWedlet;
				return newElt && isParentWedlet(newElt.wedlet) ? newElt.wedlet.visitWedletChildren(from, len, visitor) : undefined;
			}));
		}
	}

	insertChildNode(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.weight += JML.computeWeightJml([node, children]);
	}

	onChildNodesInserted() {
		this.adjustHeight();
	}

	replaceChildBind(xaOffest: number, node: IJmlNode, children: IJmlSubSet): void | Promise<void> {
		this.refreshBindValue(null);
	}

	insertAttrNode(nameAttr: string, value: string): void | Promise<void> {}

	deleteAttrNode(nameAttr: string) {}

	deleteChildNodes(xaOffest: number, count: number) {
		this.refreshBindValue(null);
	}

	updateInDescendants(msg: IXmlMsg) {
		this.refreshBindValue(null);
	}

	isEmpty(): boolean {return false}

}

const OFFVIEW_MODEL: IWedletModel = Object.freeze({
	nodeType: EUnknownNodeType.unknown,
	initModel: function (config: Element) {}
});
window.customElements.define("wed-offview", OffView);


export namespace WEDLET_SINGLEELT {

	/**
	 * Insère un noeud fils dans un IWedletSingleElt.
	 */
	export function insertChildNode(wedlet: IWedletSingleElt, xaOffset: number, node: IJmlNode, children: IJmlSubSet, childrenElts: IWedChildrenElt[], defaultWedSelector: IWedSelector, insertBefore?: Element): void | Promise<void> {
		const wedMgr = wedlet.wedMgr;
		let wedletCh: IChildWedlet & IWedletSingleElt;
		if (childrenElts) {
			const childrenElt = WEDLET.findChildrenEltForNode(childrenElts, node, wedlet, xaOffset);
			if (!childrenElt) return; //des wed:children déclarés, mais aucun match, => exclus.
			let model;
			const display = wedMgr.wedModel.findDisplayForNode(node, childrenElt);
			if (display) {
				const dispWedlet = WEDLET.findVirtualWedletFrom(display, wedlet.elementHost, insertBefore);
				if (dispWedlet) {
					if (dispWedlet.getVirtualXaPart() === xaOffset) {
						//On transforme notre bind virtuel
						dispWedlet.bindWithNode(xaOffset, node, children);
						return;
					}
				}
				model = display.wedletModel || wedMgr.wedModel.findModelForNodeFromCh(node, wedlet, childrenElt);
			} else {
				model = wedMgr.wedModel.findModelForNodeFromCh(node, wedlet, childrenElt);
			}
			wedletCh = model ? model.createWedlet(wedlet, display) as IChildWedlet & IWedletSingleElt : null;
			if (!wedletCh) return;
			wedletCh.insertElement(wedlet.elementHost, insertBefore, childrenElt.wedSlotName, childrenElt);
		} else {
			const model = wedMgr.wedModel.findModelForNode(node, null, null, wedlet, defaultWedSelector);
			wedletCh = model ? model.createWedlet(wedlet) as IChildWedlet & IWedletSingleElt : null;
			if (!wedletCh) return;
			wedletCh.insertElement(wedlet.elementHost, insertBefore);
		}
		return wedletCh.bindWithNode(xaOffset, node, children);
	}

	function findInsertPointForAttr(wedMgr: WedMgr, wedlet: IWedletSingleElt, wedletXa: IXAddr, attrName: string, slot: string | null): IWedletSingleElt {
		let found = false;
		//Si on est en init (pas de transac en cours wedMgr.currentMsg) on cherche la position de l'att d'après le schéma.
		//car docHolder.getStruct() pas utilisable en transaction: wedletXa déphasé par rapport au dom.
		const attsOrder = wedlet.model.attsOrder;
		const idx = attsOrder?.indexOf(attrName);
		let nextFound: IWedletSingleElt;
		if (idx >= 0) {
			(wedlet as IParentWedlet).visitWedletChildren(-1, Infinity, (w: IChildWedlet): 'stop' | void => {
				if (!isWedletSingleElt(w) || w.element.slot != slot) return;
				if (w.model.nodeType === ENodeType.attribute) {
					if (attsOrder.indexOf(w.model.nodeName) > idx) {
						nextFound = w;
						return 'stop';
					}
				} else if (w.model.nodeType === ENodeType.element) {
					nextFound = w;
					return 'stop';
				}
			}, WEDLET.VISITOPTIONS_includeVirtuals);
		} else {
			(wedlet as IParentWedlet).visitWedletChildren(-1, 1, (w: IChildWedlet): 'stop' | void => {
				if (!isWedletSingleElt(w)) return;
				if (w.model.nodeType === ENodeType.element) {
					nextFound = w;
					return 'stop';
				}
			}, WEDLET.VISITOPTIONS_includeVirtuals);
		}
		return nextFound;
	}

	export function insertAttrNode(wedlet: IWedletSingleElt, attrName: string, value: string, childrenElts: IWedChildrenElt[], defaultWedSelector: IWedSelector): void | Promise<void> {
		const wedMgr = wedlet.wedMgr;
		let model: IWedletModel;
		let childrenElt: IWedChildrenElt;
		if (childrenElts) {
			childrenElt = WEDLET.findChildrenEltForAttr(childrenElts, attrName);
			if (!childrenElt) return; //des wed:children déclarés, mais aucun match, => exclus.
			const display = wedMgr.wedModel.findDisplayForAttr(attrName, childrenElt);
			if (display) {
				const dispWedlet = WEDLET.findVirtualWedletFrom(display, wedlet.elementHost) as IVirtualisableWedlet & ICharsWedlet;
				if (dispWedlet) {
					//On transforme notre bind virtuel
					dispWedlet.bindWithAttr(attrName, value);
					return;
				}
				model = display.wedletModel || wedMgr.wedModel.findModelForAttr(attrName, value, childrenElt.wedModesAtts, childrenElt.wedVariants, wedlet, childrenElt.wedSelector, childrenElt.wedPreferedModels, childrenElt.wedParams);
			} else {
				model = wedMgr.wedModel.findModelForAttr(attrName, value, childrenElt.wedModesAtts, childrenElt.wedVariants, wedlet, childrenElt.wedSelector, childrenElt.wedPreferedModels, childrenElt.wedParams);
			}
		} else {
			model = wedMgr.wedModel.findModelForAttr(attrName, value, null, null, wedlet, defaultWedSelector);
		}
		const attWedlet = model ? model.createWedlet(wedlet) as ICharsWedlet & IWedletSingleElt : null;
		if (!attWedlet) return;
		//On cherche le point d'insertion en identifiant les attrs suivants dans le schéma
		let nextFound = findInsertPointForAttr(wedMgr, wedlet, wedlet.wedAnchor, attrName, childrenElt?.wedSlotName);
		attWedlet.insertElement(wedlet.elementHost, nextFound?.element, childrenElt?.wedSlotName, childrenElt);
		return attWedlet.bindWithAttr(attrName, value);
	}

	export function deleteChildNodes(wedlet: IWedletSingleElt, xaOffest: number, count: number) {
		const xaEnd = xaOffest + count;
		let ch = wedlet.elementHost.lastElementChild;
		while (ch) {
			const next = ch.previousElementSibling;
			if (IS_EltWedlet(ch)) {
				const chWedlet = ch.wedlet;
				const xaPart = chWedlet.xaPart;
				if (typeof xaPart === 'number' && xaPart >= xaOffest) {
					//node non virtuel, pas un attribut
					if (xaPart < xaEnd) {
						//On est sur le noeud à supprimer.
						if (isDisplayedWedlet(chWedlet)
							&& !(wedlet.wedMgr.wedEditor.config as OWedEditorBoxConfig).disableVirtuals
							&& !DOM.findPreviousSibling(ch, (n): n is Node => IS_EltWedlet(n) && n.wedlet.model === chWedlet.model)) {
							//On préserve l'elt qu'on bascule en virtual au lieu de le supprimer et de le réinsérer.
							//C'est une optimisation ET surtout résoud un pb d'ordonnancement des virtuals entre eux s'ils sont réinjectés après.
							// car WEDLET_SINGLEELT.adjustVirtuals() n'est pas assez précis sur ce point.
							chWedlet.bindAsVirtual();
						} else {
							if ('onDelete' in chWedlet) chWedlet.onDelete();
							ch.remove();
						}
					} else {
						chWedlet.xaPart = (xaPart as number) - count;
					}
				}
			}
			ch = next;
		}
	}

	export function deleteAttrNode(wedlet: IWedletSingleElt, attName: string) {
		for (let ch = wedlet.elementHost.firstElementChild; ch; ch = ch.nextElementSibling) {
			if (IS_EltWedlet(ch)) {
				if (ch.wedlet.xaPart === attName) {
					if ('onDelete' in ch.wedlet) ch.wedlet.onDelete();
					ch.remove();
				}
			}
		}
	}

	interface IVirtualsList extends Array<IElementWedlet> {
		hasRealBind: boolean;
	}

	/**
	 * Réévalue les noeuds virtuels fils pour ce wedlet (sauf si wedlet.elementHost@adjust-virtuals='no')
	 * Important: la liste des IWedChildrenElt {childrenElts} doit être constante pour ce wedlet.elementHost.
	 * @param wedlet
	 * @param childrenElts Liste des <wed:children/> de ce bind dans ** l'ORDRE NATUREL ** des noeuds.
	 */
	export function adjustVirtuals(wedlet: IWedletSingleElt & IParentWedlet, childrenElts: IWedChildrenElt[]) {
		const wedMgr = wedlet.wedMgr;
		if (childrenElts
			&& wedlet.elementHost.getAttribute("adjust-virtuals") !== 'no'
			&& !(wedMgr.wedEditor.config as OWedEditorBoxConfig).disableVirtuals) {
			//console.log(`adjustVirtuals`, wedlet.elementHost);
			//on est obligé de passer l'adjust en AfterBatch car on interroge le doc via docHolder.getInsertableOffset()
			//ce qui impose d'être synchro avec le doc.
			const addAfterBatch = adjustVirtualsTasks.size === 0;
			adjustVirtualsTasks.set(wedlet, childrenElts);
			if (addAfterBatch) wedMgr.doAfterBatch(adjustVirtualsExecute);
		} else {
			if (wedlet.element.onChildWedletsChange) wedlet.element.onChildWedletsChange();
		}
	}

	const adjustVirtualsTasks = new Map<IWedletSingleElt & IParentWedlet, IWedChildrenElt[]>();

	function adjustVirtualsExecute() {
		adjustVirtualsTasks.forEach((childrenElts: IWedChildrenElt[], wedlet: IWedletSingleElt & IParentWedlet) => {
			try {
				if (!wedlet.element) return; //wedlet supprimé entre temps.
				if (childrenElts.length > 1) {
					const binds = new Map<IWedChildrenElt, IVirtualsList>();
					for (let ch = DOM.findFirstChild(wedlet.elementHost, IS_EltWedlet); ch; ch = DOM.findNextSibling(ch, IS_EltWedlet)) {
						let list = binds.get(ch.fromChildrenElt);
						if (list === undefined) binds.set(ch.fromChildrenElt, (list = [] as IVirtualsList));
						if (ch.wedlet.isVirtual()) list.push(ch);
						else list.hasRealBind = true;
					}
					const xa = wedlet.wedAnchor;
					for (let i = 0; i < childrenElts.length; i++) {
						const childrenElt = childrenElts[i];
						adjustVirtualsForChildrenElt(wedlet, xa, childrenElt, binds.get(childrenElt));
					}
				} else {
					let list: IVirtualsList;
					let ch = DOM.findFirstChild(wedlet.elementHost, IS_EltWedlet);
					if (ch) {
						list = [] as IVirtualsList;
						for (; ch; ch = DOM.findNextSibling(ch, IS_EltWedlet)) if (ch.wedlet.isVirtual()) list.push(ch); else list.hasRealBind = true;
					}
					adjustVirtualsForChildrenElt(wedlet, wedlet.wedAnchor, childrenElts[0], list);
				}
			} catch (e) {
				console.error(e);
			}
			if (wedlet.doCustomAdjust) wedlet.doCustomAdjust();
		});
		adjustVirtualsTasks.clear();
	}

	function adjustVirtualsForChildrenElt(wedlet: IWedletSingleElt & IParentWedlet, xa: IXAddr, childrenElt: IWedChildrenElt, virtualsList: IVirtualsList) {
		if (childrenElt.wedDefaultDisplay) {
			if (!virtualsList || !virtualsList.hasRealBind) {
				adjustVirtualsForDisplay(wedlet, xa, childrenElt, childrenElt.wedDefaultDisplay, virtualsList);
			}
		} else if (childrenElt.wedDisplays) {
			for (let i = 0; i < childrenElt.wedDisplays.length; i++) {
				adjustVirtualsForDisplay(wedlet, xa, childrenElt, childrenElt.wedDisplays[i], virtualsList);
			}
		}
		//cleanup des virtuals non traités.
		if (virtualsList) for (let i = 0; i < virtualsList.length; i++) {
			const eltWedlet = virtualsList[i];
			if (eltWedlet && isDisplayedWedlet(eltWedlet.wedlet)) eltWedlet.wedlet.deleteVirtualWedlet();
		}
	}

	function adjustVirtualsForDisplay(wedlet: IWedletSingleElt & IParentWedlet, xa: IXAddr, childrenElt: IWedChildrenElt, displayElt: IWedDisplayElt, virtualsList: IVirtualsList) {
		const modes = displayElt.wedNodeType === ENodeType.attribute ? childrenElt.wedModesAtts : childrenElt.wedModesNodes;
		const model = displayElt.wedletModel || wedlet.wedMgr.wedModel.findModelForVirtual(displayElt.wedNodeType, displayElt.wedNodeName, modes, childrenElt.wedVariants, wedlet, childrenElt.wedSelector, childrenElt.wedPreferedModels, childrenElt.wedParams);
		if (model) {
			if (model.config.hasAttribute("denyVirtual")) return;
			const insertBefore = findInsertBeforeVirtual(wedlet, xa, model, childrenElt.wedSlotName);
			if (insertBefore !== 'noInsert') {
				//insertBefore == 'noInsert': pas d'insertion possible (noeud existe déjà et card 0-1) ou modèle est inconnu dans le schéma
				if (!isVirtualAlreadyInPlace(insertBefore ? insertBefore.previousSibling : wedlet.elementHost.lastChild, model, virtualsList)) {
					const displayedWedlet = model.createWedlet(wedlet, displayElt) as IVirtualisableWedlet;
					if (displayedWedlet) {
						if (!isVirtualRecursive(wedlet, model)) {
							displayedWedlet.insertElement(wedlet.elementHost, insertBefore, childrenElt.wedSlotName, childrenElt);
							displayedWedlet.bindAsVirtual();
						} else if ('switchToRecursiveVirtualButton' in displayedWedlet) {
							displayedWedlet.switchToRecursiveVirtualButton();
							displayedWedlet.insertElement(wedlet.elementHost, insertBefore, childrenElt.wedSlotName, childrenElt);
							//displayedWedlet.bindAsVirtual(); non sinon les displayedWedlet.childrenElts sont injectés récursivement.
						}
					}
				}
			}
		} else {
			console.log("Display not found for ", childrenElt);
		}
	}

	/** controle de la récursivité infinie des virtuals. */
	function isVirtualRecursive(parent: IWedlet, childModel: IWedletModel): boolean {
		while (parent && parent.isVirtual()) {
			if (parent.model === childModel) return true;
			//non : cas de sp:co/*/sp:co par ex.
			//const parentM = parent.model;
			//if (parentM && parentM.nodeName === childModel.nodeName && parentM.nodeType === childModel.nodeType) return true;
			parent = parent.wedParent;
		}
		return false;
	}

	function isVirtualAlreadyInPlace(elt: Node, childModel: IWedletModel, virtualsList: IVirtualsList): boolean {
		while (elt) {
			if (IS_EltWedlet(elt)) {
				if (!elt.wedlet.isVirtual() && childModel.nodeType !== ENodeType.attribute) {
					//element non viruel, donc childModel pas trouvé
					//Mais si childModel est un attr, les attr ne sont pas ordonnés, donc on continue à atrvers les noeuds réels
					return false;
				}
				if (elt.wedlet.model === childModel) {
					//à priori déjà présent sauf si est issu d'un autre childrenElt : sa suppr de virtualsList va le valider.
					if (virtualsList) for (let i = 0; i < virtualsList.length; i++) {
						if (virtualsList[i] === elt) {
							//Trouvé dans la liste on l'élimine pour ne pas le purger.
							virtualsList[i] = null;
							return true;
						}
					}
				}
			}
			elt = elt.previousSibling;
		}
		return false;
	}

	function findInsertBeforeVirtual(wedlet: IWedletSingleElt & IParentWedlet, xa: IXAddr, model: IWedletModel, slot: string | null): Node | 'noInsert' {
		const offset = wedlet.isVirtual() ? 0 : wedlet.wedMgr.docHolder.getInsertableOffset(xa, model.nodeType as ENodeType, 'reject', model.nodeName);
		if (offset < 0) return 'noInsert';
		if (model.nodeType === ENodeType.attribute) {
			const nextWedlet = findInsertPointForAttr(wedlet.wedMgr, wedlet, xa, model.nodeName, slot);
			return nextWedlet?.element;
		} else {
			const nextWedlet = wedlet.findWedletChild(offset, WEDLET.VISITOPTIONS_mainBranch) as IChildWedlet;
			let nextS: Node = isWedletSingleElt(nextWedlet) ? nextWedlet.element : null;
			//on se place après les virtuels et les comments déjà présents à ce point d'insertion.
			while (IS_EltWedlet(nextS) && (nextS.wedlet.isVirtual() || nextS.wedlet.model.nodeType === ENodeType.comment)) nextS = nextS.nextSibling;
			return nextS;
		}
	}

	/** Nettoie ce wedlet dans le but de devenir virtuel. */
	export function clearChildrenAsVirtual(wedlet: IWedletSingleElt) {
		const elt = wedlet.elementHost;
		let ch = elt.firstElementChild;
		while (ch) {
			const next = ch.nextElementSibling;
			if (IS_EltWedlet(ch)) {
				if (ch.wedlet !== wedlet) {
					//ce noeud n'appartient à ce wedlet, on purge
					if ('onDelete' in ch.wedlet) ch.wedlet.onDelete();
					ch.remove();
				} else if (isDisplayedWedlet(ch.wedlet) && !isWedDefaultDisplay(ch.wedlet.displayCtx)) {
					ch.wedlet.bindAsVirtual();
				}
			}
			ch = next;
		}
	}

	/**
	 * Gestion du focus du nouveau IElementWedlet lorsqu'il a été supprimé par virtualisation de son
	 * son contexte parent sur une supression.
	 */
	export function reselectOnVirtualizing(elt: IElementWedlet) {
		try {
			let w = elt.wedlet as IWedletSingleElt & IParentWedlet;
			const wedletStack = [];
			while (!w.element || !w.element.isConnected) {
				wedletStack.push(w);
				w = w.wedParent as IParentWedlet & IWedletSingleElt;
			}
			for (let i = wedletStack.length - 1; i >= 0; i--) {
				const target = wedletStack[i];
				w.visitWedletChildren(-1, Infinity, (ch: IChildWedlet) => {
					if (ch.isVirtual() && ch.model === target.model) {
						w = ch;
						return 'stop';
					}
				}, {includeVirtuals: true});
			}
			w.focusWedlet();
		} catch (e) {}//tentative au mieux de resel le widget, pas d'exception si echec
	}
}


